'use client';

import { useAtomValue } from 'jotai';
import { ReactElement, ReactNode, cloneElement, createContext, isValidElement, use } from 'react';

import { TextButton, TextButtonProps } from '@flipster/react/design-system/text-button';
import { Styles, css } from '@flipster/styled-system/css';
import { circle } from '@flipster/styled-system/patterns';

import { rawCurrenciesKeyByCurrencyAtom, rawInstrumentsKeyBySymbolAtom } from '../shared/snapshot/state';

const IconContext = createContext<{ icon?: string; shortName?: string; fullName?: string }>({});

const Layout = ({
  hasSubLabel,
  css: cssProps,
  children,
}: {
  hasSubLabel: boolean;
  css?: Styles;
  children: ReactNode;
}) => {
  return (
    <div
      className={css(
        {
          display: 'inline-grid',
          gridTemplateAreas: hasSubLabel
            ? `
            'icon label'
            'icon subLabel'
          `
            : "'icon label'", // 이렇게하지 않으면 sub label 영역이 무조건 있는걸로 인식해서 세로 정렬 불가
          alignItems: 'center',
          justifyContent: 'flex-start',
        },
        cssProps,
      )}
    >
      {children}
    </div>
  );
};

const Display = ({ size = 20, space = 6 }: { size?: number; space?: number }) => {
  const { icon } = use(IconContext);
  return (
    <div
      className={circle({
        bgColor: 'background.default.level-1',
        overflow: 'hidden',
        gridArea: 'icon',
        size,
        mr: space,
      })}
    >
      {icon && <img data-role='symbol-icon' src={icon} alt='icon' className={css({ w: '100%', h: 'auto' })} />}
    </div>
  );
};

interface LabelProps {
  area?: 'main' | 'sub';
  value?: 'shortName' | 'fullName';
  withParentheses?: boolean;
  children?: ReactNode;
  css?: Styles;
}

const Label = ({ area = 'main', value, withParentheses, children, css: cssProps }: LabelProps) => {
  const { shortName, fullName } = use(IconContext);
  return (
    <div className={css({ gridArea: area === 'main' ? 'label' : 'subLabel', textAlign: 'left' }, cssProps)}>
      {value && value === 'shortName' && <span>{shortName}</span>}
      {value && value === 'fullName' && <span>{withParentheses && fullName ? `(${fullName})` : fullName}</span>}
      {children}
    </div>
  );
};

const SymbolDisplayBase = ({
  symbol,
  icon,
  mainLabel,
  subLabel,
  css: cssProps,
}: {
  symbol: string;
  icon?: ReactNode;
  mainLabel?: ReactNode;
  subLabel?: ReactNode;
  css?: Styles;
}) => {
  const instrumentsKeyBySymbol = useAtomValue(rawInstrumentsKeyBySymbolAtom);
  const currentSymbol = instrumentsKeyBySymbol?.[symbol];

  return (
    <IconContext
      value={{
        icon: currentSymbol?.icon,
        shortName: currentSymbol?.displayShortName,
        fullName: currentSymbol?.displayName,
      }}
    >
      <Layout css={cssProps} hasSubLabel={!!subLabel}>
        <>
          {icon}
          {isValidElement(mainLabel) && mainLabel.type === Label
            ? cloneElement(mainLabel as ReactElement<LabelProps>, { area: 'main' })
            : mainLabel}
          {isValidElement(subLabel) && subLabel.type === Label
            ? cloneElement(subLabel as ReactElement<LabelProps>, { area: 'sub' })
            : subLabel && <Label area='sub'>{subLabel}</Label>}
        </>
      </Layout>
    </IconContext>
  );
};

export const SymbolTextButton = <T extends 'button' | 'a' | undefined = 'button'>({
  symbol,
  ...textButtonProps
}: {
  symbol: string;
} & TextButtonProps<T>) => {
  const instrumentsKeyBySymbol = useAtomValue(rawInstrumentsKeyBySymbolAtom);
  const currentSymbol = instrumentsKeyBySymbol?.[symbol];
  return (
    <TextButton
      icon={<img src={currentSymbol?.icon} alt='icon' />}
      label={currentSymbol?.displayShortName}
      {...textButtonProps}
    />
  );
};

const CurrencyDisplayBase = ({
  currency,
  icon,
  mainLabel,
  subLabel,
  css: cssProps,
}: {
  currency: string;
  icon?: ReactNode;
  mainLabel?: ReactNode;
  subLabel?: ReactNode;
  css?: Styles;
}) => {
  const currenciesKeyByCurrency = useAtomValue(rawCurrenciesKeyByCurrencyAtom);
  const currentCurrency = currenciesKeyByCurrency?.[currency];

  return (
    <IconContext
      value={{
        icon: currentCurrency?.icon,
        shortName: currentCurrency?.currency,
        fullName: currentCurrency?.description,
      }}
    >
      <Layout css={cssProps} hasSubLabel={!!subLabel}>
        <>
          {icon}
          {isValidElement(mainLabel) && mainLabel.type === Label
            ? cloneElement(mainLabel as ReactElement<LabelProps>, { area: 'main' })
            : mainLabel}
          {isValidElement(subLabel) && subLabel.type === Label
            ? cloneElement(subLabel as ReactElement<LabelProps>, { area: 'sub' })
            : subLabel && <Label area='sub'>{subLabel}</Label>}
        </>
      </Layout>
    </IconContext>
  );
};

export const SymbolDisplay = Object.assign(SymbolDisplayBase, { Icon: Display, Label });
export const CurrencyDisplay = Object.assign(CurrencyDisplayBase, { Icon: Display, Label });
